import { wccModules } from 'enums/wccModules';
import { TextHelpers } from 'helpers/text';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import { Person } from 'models/person';
import { Topic } from 'models/topic';
import doubleLabelPattern from 'patterns/doubleLabel';
import './stageMessage.html';
import './stageMessage.scss';

components.preload('wcc-alert');

export interface TaskActivityIdeationStageMessageVMConfig {
    topicId: SubscribableOrNullableValue<string>
}

interface MessageConfig {
    title?: string
    content: string
    votesCount?: boolean
}

class Message {
    title: string
    content: string
    votesCountShown: boolean

    titleShown: boolean

    constructor(config: MessageConfig) {
        this.title = config.title ?? '';
        this.content = config.content;
        this.votesCountShown = config.votesCount ?? false;
        this.titleShown = this.title.length > 0;
    }
}

@injectable()
export default class TaskActivityIdeationStageMessageVM {
    private user: Subscribable<Person | undefined>
    private topic: Subscribable<Topic | undefined>

    private availableUpvotes: Subscribable<number>
    private availableDownvotes: Subscribable<number>

    topicId: Subscribable<string | undefined>

    message = ko.pureComputed(() => this.getMessage());

    constructor(
        @inject(wccModules.componentConfig) config: TaskActivityIdeationStageMessageVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.topicId = ko.flattenComputed(config.topicId);

        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        this.user = userManager.pluck(m => m.person);

        const topicManager = storage.get(TopicManager, { topicId: this.topicId });
        this.topic = topicManager.pluck(t => t.topic);

        const ideationManager = storage.get(IdeationManager, { topicId: this.topicId });
        const ideationManagerReady = ideationManager.pluck(m => m.isReady, false);

        this.availableUpvotes = ideationManager.pluck(m => m.availableUpvotes, 0);
        this.availableDownvotes = ideationManager.pluck(m => m.availableDownvotes, 0);

        const isReady = ko.pureComputed(() => {
            return this.user() != undefined &&
                this.topic() != undefined &&
                ideationManagerReady() &&
                this.message() != undefined;
        });

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'task-activity-ideation-stage-message': true
            }
        });
    }

    private getMessage(): Message | undefined {
        const user = this.user();
        const topic = this.topic();

        if (user != undefined && topic != undefined) {
            switch (topic.ideationStage()) {
                case enums.IdeationStages.IdeaReview.value:
                    return user.isModerator() ?
                        new Message({ content: messages.YouAreInStage2OfIdeation }) :
                        new Message({ title: contentMessages.IdeationReviewInProgress.Title, content: contentMessages.IdeationReviewInProgress.Content });

                case enums.IdeationStages.IdeaRating.value:
                    return user.isRegular() && this.availableUpvotes() > 0 ?
                        new Message({ content: this.getRankingMessageContent(topic), votesCount: true }) :
                        undefined;
            }
        }
    }

    private getRankingMessageContent(topic: Topic) {
        if (topic.ideationDownvotes() > 0) {
            return labels.YouHaveNUpvotesAndMDownvotes
                .replace('{0}', this.availableUpvotes().toString())
                .replace('{1}', this.availableDownvotes().toString())
                .replace(doubleLabelPattern, label => TextHelpers.selectLabelFromDoubleLabel(label, this.availableUpvotes()))
                .replace(doubleLabelPattern, label => TextHelpers.selectLabelFromDoubleLabel(label, this.availableDownvotes()));
        }

        return labels
            .YouHaveNUpvotes
            .replace('{0}', this.availableUpvotes().toString())
            .replace(doubleLabelPattern, label => TextHelpers.selectLabelFromDoubleLabel(label, this.availableUpvotes()));
    }
}